import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { FileText, MessageSquare, UserCheck, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import { useSettings } from '../../contexts/SettingsContext';
import KpiCards from '../../components/Admin/Dashboard/KpiCards';
import PendingPanel from '../../components/Admin/Dashboard/PendingPanel';
import DashboardCharts from '../../components/Admin/Dashboard/DashboardCharts';
import LeadsSection from '../../components/Admin/Dashboard/LeadsSection';
import { DashboardOverview } from '../../components/Admin/Dashboard/types';
import { Button } from '../../components/UI';

interface RecentActivity {
  id: string;
  type: string;
  description: string;
  timestamp: string;
  user?: { name: string; email: string };
  metadata?: { title?: string; subject?: string; [key: string]: any };
}

// Pendências mudam ao longo do dia; recarrega em segundo plano sem mostrar o spinner
const REFRESH_MS = 60 * 1000;

const formatActivityTime = (timestamp: string, t: TFunction) => {
  const diffInMinutes = Math.floor((Date.now() - new Date(timestamp).getTime()) / (1000 * 60));
  if (diffInMinutes < 1) return t('dashboardHome.time.now');
  if (diffInMinutes < 60) return t('dashboardHome.time.minutes', { count: diffInMinutes });
  if (diffInMinutes < 1440) return t('dashboardHome.time.hours', { count: Math.floor(diffInMinutes / 60) });
  return t('dashboardHome.time.days', { count: Math.floor(diffInMinutes / 1440) });
};

const activityIcons: Record<string, typeof UserCheck> = {
  user: UserCheck,
  post: FileText,
  contact: MessageSquare,
};

const AdminDashboard: React.FC = () => {
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const notifications = useNotifications();
  const { settings } = useSettings();
  const { t, i18n } = useTranslation();
  const siteName = settings?.site?.name || t('common.siteNameFallback');

  const fetchDashboardData = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [overviewResponse, activitiesResponse] = await Promise.all([
        axios.get('/dashboard/overview'),
        axios.get('/activities'),
      ]);
      setOverview(overviewResponse.data);
      setActivities(activitiesResponse.data?.activities || []);
      setUpdatedAt(new Date());
      setError('');
    } catch (error: any) {
      console.error('Erro:', error);
      if (!silent) {
        setError(t('dashboardHome.loadError'));
        notifications.error.network();
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const timer = setInterval(() => fetchDashboardData(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchDashboardData]);

  const recentActivities = useMemo(() => activities.slice(0, 6).map((activity) => {
    let subtitle = activity.user?.name || '';
    if (activity.type === 'post' && activity.metadata?.title) {
      subtitle = activity.metadata.title;
    } else if (activity.type === 'contact' && activity.metadata?.subject) {
      subtitle = activity.metadata.subject;
    }
    return {
      title: activity.description,
      description: subtitle,
      time: formatActivityTime(activity.timestamp, t),
      icon: activityIcons[activity.type] || Clock,
    };
  }), [activities, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('dashboardHome.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error || t('dashboardHome.loadError')}</p>
          <Button onClick={() => fetchDashboardData()}>
            {t('dashboardHome.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('admin.dashboard')}</h1>
              <p className="text-gray-600">{t('dashboardHome.subtitle', { site: siteName })}</p>
            </div>
            {updatedAt && (
              <div className="text-sm text-gray-500">
                {t('dashboardHome.updatedAt', {
                  time: updatedAt.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <KpiCards kpis={overview.kpis} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2">
            <PendingPanel items={overview.pending} />
          </div>

          {/* Recent Activities */}
          <div>
            <div className="card h-full">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 md:mb-6">{t('dashboardHome.recentActivities')}</h2>
              {recentActivities.length === 0 ? (
                <p className="text-sm text-gray-500">{t('dashboardHome.noActivities')}</p>
              ) : (
                <div className="space-y-3 md:space-y-4">
                  {recentActivities.map((activity, index) => {
                    const Icon = activity.icon;
                    return (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                          <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{activity.description}</p>
                          <p className="text-xs text-gray-500">{activity.time}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {overview.leads && <LeadsSection leads={overview.leads} />}

        <DashboardCharts charts={overview.charts} />
      </div>
    </div>
  );
};

export default AdminDashboard;
